import { useState, useEffect } from 'preact/hooks';
import { useGestures } from '../../hooks/useGestures';
import { ImageComparison } from './ImageComparison';
import type { ChibiImage } from '../../types';

interface ImageViewerProps {
  image: ChibiImage;
  isOpen: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

export function ImageViewer({ image, isOpen, onClose, onNext, onPrevious }: ImageViewerProps) {
  const [comparisonMode, setComparisonMode] = useState<'flip' | 'slider' | 'side-by-side'>('flip');

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const gestures = useGestures({
    onSwipeLeft: onNext,
    onSwipeRight: onPrevious,
    onSwipeDown: onClose,
    onLongPress: () => setComparisonMode('slider'),
    onDoubleTap: () => setComparisonMode('side-by-side'),
  });

  if (!isOpen) return null;

  return (
    <div class="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        class="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-white/10 rounded-full text-white touch-target"
        aria-label="Close viewer"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Navigation buttons */}
      {onPrevious && (
        <button
          onClick={onPrevious}
          class="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/10 rounded-full text-white touch-target"
          aria-label="Previous image"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      
      {onNext && (
        <button
          onClick={onNext}
          class="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/10 rounded-full text-white touch-target"
          aria-label="Next image"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Image comparison container */}
      <div 
        ref={gestures.ref}
        class="relative w-full h-full flex items-center justify-center p-4"
      >
        <ImageComparison 
          image={image} 
          mode={comparisonMode}
          onModeChange={setComparisonMode}
        />
      </div>

      {/* Mode switcher buttons */}
      <div class="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 rounded-full p-1">
        <button
          onClick={() => setComparisonMode('flip')}
          class={`px-3 py-1 rounded-full text-sm transition-colors ${
            comparisonMode === 'flip' ? 'bg-white text-black' : 'text-white hover:bg-white/20'
          }`}
        >
          Flip
        </button>
        <button
          onClick={() => setComparisonMode('slider')}
          class={`px-3 py-1 rounded-full text-sm transition-colors ${
            comparisonMode === 'slider' ? 'bg-white text-black' : 'text-white hover:bg-white/20'
          }`}
        >
          Slider
        </button>
        <button
          onClick={() => setComparisonMode('side-by-side')}
          class={`px-3 py-1 rounded-full text-sm transition-colors ${
            comparisonMode === 'side-by-side' ? 'bg-white text-black' : 'text-white hover:bg-white/20'
          }`}
        >
          Side by Side
        </button>
      </div>

      {/* Image info */}
      <div class="absolute bottom-4 left-4 right-4 text-center">
        <div class="flex justify-center gap-2 mb-2">
          {image.tags.map(tag => (
            <span key={tag} class="px-2 py-1 bg-white/20 text-white text-xs rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}